import React, { useState, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { 
  FileText, 
  Layers, 
  Clock, 
  Users, 
  UserPlus, 
  MessageSquare, 
  HelpCircle, 
  Plus,
  Hourglass,
  DollarSign,
  BarChartIcon,
  CalendarDays
} from "lucide-react";
import { cn } from "@/lib/utils";
import LegalAnalytics from "@/components/LegalAnalytics";

// Action card data - updated with new titles
const actionCards = [
  {
    id: 1,
    title: "Single Matter",
    description: "Request proposals or send instructions for a single matter/project appointments.",
    icon: FileText,
    comingSoon: true,
  },
  {
    id: 2,
    title: "Portfolio",
    description: "Create a bundled RFP/Instruction covering multiple related matters.",
    icon: Layers,
    comingSoon: true,
  },
  {
    id: 3,
    title: "Billable Hour",
    description: "Propose, review and finalise billable hour terms with law firms.",
    icon: Clock,
    comingSoon: true,
  },
  {
    id: 4,
    title: "Panel Appointment",
    description: "Invite firms to join or renew panel/vendor arrangements.",
    icon: Users,
    comingSoon: true,
  },
  {
    id: 5,
    title: "Secondments",
    description: "Request secondees or legal staffing support for your team.",
    icon: UserPlus,
    comingSoon: true,
  },
  {
    id: 6,
    title: "Negotiate",
    description: "Renegotiate scope, rates or terms on current matters.",
    icon: MessageSquare,
    comingSoon: true,
  },
  {
    id: 7,
    title: "Information Requests",
    description: "Send information requests to firms.",
    icon: HelpCircle,
    comingSoon: true,
  },
  {
    id: 8,
    title: "Create New",
    description: "",
    icon: Plus,
    comingSoon: false,
    isCreateNew: true,
  },
];

// Action Card Component
const ActionCard = ({ 
  title, 
  description, 
  icon: Icon, 
  comingSoon, 
  isCreateNew,
  onClick 
}: {
  title: string;
  description: string;
  icon: React.ElementType;
  comingSoon: boolean;
  isCreateNew?: boolean;
  onClick?: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (isCreateNew) {
    return (
      <Card 
        className="relative cursor-pointer border-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary transition-all duration-300 group shadow-sm"
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CardContent className="flex flex-col items-center justify-center p-6 h-full min-h-[180px]">
          <div className={cn(
            "rounded-xl p-4 mb-4 transition-all duration-300",
            "bg-primary/10 group-hover:bg-primary/20"
          )}>
            <Icon className="h-8 w-8 text-primary" />
          </div>
          <h3 className="font-semibold text-foreground text-center text-base">{title}</h3>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className="relative cursor-pointer border border-border bg-card hover:shadow-lg transition-all duration-300 overflow-hidden group shadow-sm"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="flex flex-col items-center justify-center p-6 h-full min-h-[180px]">
        <div className={cn(
          "rounded-xl p-4 mb-4 transition-all duration-300",
          "bg-[hsl(var(--action-icon-bg))]"
        )}>
          <Icon className="h-8 w-8 text-primary" />
        </div>
        <h3 className="font-semibold text-foreground text-center text-base leading-tight mb-2">{title}</h3>
        <p className="text-muted-foreground text-center text-xs leading-relaxed">{description}</p>
      </CardContent>
      
      {/* Coming Soon Overlay - Frosted Glass */}
      {comingSoon && (
        <div className={cn(
          "absolute inset-0 flex flex-col items-center justify-center transition-all duration-300",
          "bg-background/70 backdrop-blur-md",
          isHovered ? "opacity-100" : "opacity-0 pointer-events-none"
        )}>
          <Hourglass className="h-10 w-10 text-muted-foreground mb-3" />
          <span className="text-sm font-semibold text-muted-foreground">Coming soon</span>
        </div>
      )}
    </Card>
  );
};

// Empty State Placeholder for toggles
const EmptyStatePlaceholder = ({ text }: { text: string }) => (
  <div className="w-full min-h-[300px] border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center bg-muted/20">
    <Hourglass className="h-12 w-12 text-muted-foreground mb-4" />
    <p className="text-muted-foreground text-center font-medium">{text}</p>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("use-case");
  const knowledgeRef = useRef<HTMLDivElement>(null);

  // Render the title in the outlet context
  if (useOutletContext() === "title-only") {
    return "Dashboard";
  }

  const handleCreateNew = () => {
    navigate("/new");
  };

  const scrollToKnowledge = () => {
    knowledgeRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  return (
    <div className="animate-fade-in py-8 px-4">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Welcome back</h1>
        <p className="text-muted-foreground text-lg">Ready to launch your next legal engagement?</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 mb-8">
          <TabsTrigger 
            value="use-case" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 text-sm font-semibold"
          >
            Use Case
          </TabsTrigger>
          <TabsTrigger 
            value="practice-group" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 text-sm font-semibold"
          >
            Practice Group
          </TabsTrigger>
          <TabsTrigger 
            value="industry" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 text-sm font-semibold"
          >
            Industry
          </TabsTrigger>
        </TabsList>

        {/* Use Case Tab Content - 2x4 Action Grid */}
        <TabsContent value="use-case" className="mt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {actionCards.map((card) => (
              <ActionCard
                key={card.id}
                title={card.title}
                description={card.description}
                icon={card.icon}
                comingSoon={card.comingSoon}
                isCreateNew={card.isCreateNew}
                onClick={card.isCreateNew ? handleCreateNew : undefined}
              />
            ))}
          </div>
        </TabsContent>

        {/* Practice Group Tab Content */}
        <TabsContent value="practice-group" className="mt-0">
          <EmptyStatePlaceholder text="Templates coming soon" />
        </TabsContent>

        {/* Industry Tab Content */}
        <TabsContent value="industry" className="mt-0">
          <EmptyStatePlaceholder text="Templates coming soon" />
        </TabsContent>
      </Tabs>

      <Separator className="my-8" />

      {/* Knowledge Section */}
      <section ref={knowledgeRef} id="knowledge">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-foreground">Knowledge</h2>
          <p className="text-muted-foreground text-sm">Gain insights into your legal expenditure</p>
        </div>
        
        {/* Balanced Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Left Column - Core Analytics (2/3 width) */}
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Monthly Legal Expenditure */}
            <Card className="border border-border bg-card hover:shadow-md transition-all duration-300">
              <CardContent className="p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-foreground">Monthly Legal Expenditure</h3>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-h-[140px]">
                  <LegalAnalytics variant="monthly" />
                </div>
              </CardContent>
            </Card>

            {/* Regional Legal Expenditure */}
            <Card className="border border-border bg-card hover:shadow-md transition-all duration-300">
              <CardContent className="p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-foreground">Regional Legal Expenditure</h3>
                  <BarChartIcon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-h-[140px]">
                  <LegalAnalytics variant="regional" />
                </div>
              </CardContent>
            </Card>

            {/* Year-over-Year Comparison */}
            <Card className="border border-border bg-card hover:shadow-md transition-all duration-300">
              <CardContent className="p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-foreground">Year-over-Year Comparison</h3>
                  <CalendarDays className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-h-[140px]">
                  <LegalAnalytics variant="yoy" />
                </div>
              </CardContent>
            </Card>

            {/* Expenditure by Practice Group */}
            <Card className="border border-border bg-card hover:shadow-md transition-all duration-300">
              <CardContent className="p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-foreground">Expenditure by Practice Group</h3>
                  <BarChartIcon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-h-[140px]">
                  <LegalAnalytics variant="practice" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Right Column - Featured Insights (1/3 width) */}
          <div className="md:col-span-1 flex flex-col gap-4">
            <Card className="border border-border bg-card hover:shadow-md transition-all duration-300 flex-1">
              <CardContent className="p-5 h-full flex flex-col items-center justify-center text-center">
                <div className="rounded-xl p-3 bg-[hsl(var(--action-icon-bg))] mb-3">
                  <DollarSign className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-sm font-medium text-foreground mb-1">Average Expenditure per Matter</h3>
                <p className="text-3xl font-bold text-foreground">$48,500</p>
                <p className="text-xs text-green-600 mt-2 font-medium">+5.2% vs last year</p>
              </CardContent>
            </Card>
            
            <Card className="border border-border bg-card hover:shadow-md transition-all duration-300 flex-1">
              <CardContent className="p-5 h-full flex flex-col items-center justify-center text-center">
                <div className="rounded-xl p-3 bg-[hsl(var(--action-icon-bg))] mb-3">
                  <BarChartIcon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-sm font-medium text-foreground mb-1">Amount vs Budget</h3>
                <p className="text-3xl font-bold text-foreground">83%</p>
                <div className="w-full max-w-[120px] bg-muted rounded-full h-2 mt-3">
                  <div className="bg-primary h-2 rounded-full" style={{ width: "83%" }}></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">$746,000 / $900,000</p>
              </CardContent>
            </Card>
            
            <Card className="border border-border bg-card hover:shadow-md transition-all duration-300 flex-1">
              <CardContent className="p-5 h-full flex flex-col items-center justify-center text-center">
                <div className="rounded-xl p-3 bg-[hsl(var(--action-icon-bg))] mb-3">
                  <CalendarDays className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-sm font-medium text-foreground mb-1">Matters per Month</h3>
                <p className="text-3xl font-bold text-foreground">12</p>
                <p className="text-xs text-orange-600 mt-2 font-medium">-2.1% vs previous month</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;